/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, useState, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '../../shared/types/common.types';
import { authStorage } from '../../shared/lib/authStorage';

type AuthContextType = {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    login: (user: User, token: string) => void;
    logout: () => void;
    updateUser: (user: User) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate();

    const [user, setUser] = useState<User | null>(() => {
        return authStorage.getUser() as User | null;
    });

    const [token, setToken] = useState<string | null>(() => {
        return authStorage.getAccessToken();
    });

    const login = useCallback((newUser: User, newToken: string) => {
        authStorage.setAccessToken(newToken);
        authStorage.setUser(newUser);

        setToken(newToken);
        setUser(newUser);
    }, []);

    const logout = useCallback(() => {
        authStorage.clearTokens();

        setToken(null);
        setUser(null);
        navigate('/');
    }, [navigate]);

    const updateUser = useCallback((updatedUser: User) => {
        authStorage.setUser(updatedUser);
        setUser(updatedUser);
    }, []);

    const value = useMemo(
        () => ({
            user,
            token,
            isAuthenticated: !!token,
            login,
            logout,
            updateUser,
        }),
        [user, token, login, logout, updateUser]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth harus digunakan di dalam AuthProvider.');
    }
    return context;
};
